import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  useToast,
  useColorModeValue,
  Textarea,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import CrossChainFeeSettings from '../components/CrossChainFeeSettings';

const SettingsPage: React.FC = () => {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await api.settings.getProfile();
      return response.data.partner;
    },
  });

  // Function to adjust textarea height
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Adjust height when profile data changes
  React.useEffect(() => {
    adjustTextareaHeight();
  }, [profile?.apiKey]);

  // Adjust height when window is resized
  React.useEffect(() => {
    const handleResize = () => {
      adjustTextareaHeight();
    };

    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box>
      <Heading mb={8}>Settings</Heading>

      <VStack spacing={8} align="stretch">
        {/* Profile Information */}
          <Box p={6} bg={cardBg} rounded="lg" border="1px" borderColor={borderColor}>
          <Heading size="md" mb={6}>
            Profile Information
          </Heading>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input value={profile?.email || ''} isReadOnly />
            </FormControl>

            <FormControl>
              <FormLabel>API Key</FormLabel>
              <VStack spacing={2} align="stretch">
                <Textarea
                  ref={textareaRef}
                  value={profile?.apiKey || ''}
                  isReadOnly
                  resize="none"
                  fontFamily="mono"
                  fontSize="sm"
                  overflow="hidden"
                  minH="40px"
                  onInput={adjustTextareaHeight}
                />
                <Button
                  size="sm"
                  h="1.75rem"
                  alignSelf="flex-end"
                  onClick={() => {
                    if (profile?.apiKey) {
                      navigator.clipboard.writeText(profile.apiKey);
                      toast({
                        title: 'Copied!',
                        description: 'API Key copied to clipboard',
                        status: 'success',
                        duration: 2000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  Copy
                </Button>
              </VStack>
            </FormControl>
          </VStack>
        </Box>

        {/* Spark: USDC/USDT fees */}
        <CrossChainFeeSettings />
      </VStack>
    </Box>
  );
};

export default SettingsPage; 